/** Canvas OS File Drop — per-media-type client-side pre-flight size caps
 * (VistoeraTASK-00220 §十一/§十二 — "拖入明显超限文件必须立即拒绝，而不是
 * 上传半天再失败"; every value below is taken from a REAL existing
 * backend limit for that exact upload transport, never invented):
 *
 *  - AUDIO: mirrors `audio_asset_library_service._MAX_UPLOAD_BYTES`
 *    (150MB) — the backend's real, enforced cap for `/audio-assets/upload`.
 *  - VIDEO: mirrors `Settings.video_gen_video_max_size_mb` (300MB) — the
 *    backend's real, enforced (streamed, checked mid-transfer) cap for
 *    `/video-assets/upload`.
 *  - IMAGE: the canonical `SiteAsset` upload route
 *    (`POST /workspaces/{id}/assets`, used by `uploadSiteAsset`) has NO
 *    backend size limit at all today (forensically confirmed — see
 *    TASK-00220 forensic report). Rather than invent an arbitrary number
 *    from nothing, this borrows the closest existing real precedent in
 *    this exact codebase for "a tenant uploads one photo-sized image"
 *    (`Settings.ai_chat_image_max_size_mb` = 20MB, AI Chat's own image
 *    upload cap) as a client-side-only guard. Adding a matching
 *    backend-side limit to the SiteAsset route is out of this task's
 *    scope (no forensic evidence oversized images are the reported
 *    failure mode) — tracked as a FOLLOW_UP in the final report, not
 *    silently skipped.
 */
import type { CanvasMediaKind } from './canvasFileDrop'

export const CANVAS_MEDIA_IMPORT_MAX_BYTES: Record<CanvasMediaKind, number> = {
  image: 20 * 1024 * 1024,
  audio: 150 * 1024 * 1024,
  video: 300 * 1024 * 1024,
}

export function formatMaxSizeMb(kind: CanvasMediaKind): number {
  return Math.round(CANVAS_MEDIA_IMPORT_MAX_BYTES[kind] / (1024 * 1024))
}

/** True when `file` is small enough to even attempt an upload for
 * `kind` — callers must reject BEFORE creating any placeholder node or
 * issuing any network request (spec §十一 "立即提示，而不是上传半天再
 * 失败"). */
export function isWithinCanvasMediaImportSizeLimit(file: Pick<File, 'size'>, kind: CanvasMediaKind): boolean {
  return file.size <= CANVAS_MEDIA_IMPORT_MAX_BYTES[kind]
}
